/**
 * Workflow row-shape schemas for the Phase 1 redesign.
 *
 * The structural request/response schemas (ZWorkflowBody, ZTrigger,
 * ZStep, ZTransition, ZAction, ZCondition, ZCopyBody, ZListQuery)
 * live in `../workflow/zod.ts`. This file holds:
 *
 *   - the Prisma row shapes (Workflow, WorkflowStep, WorkflowTransition,
 *     WorkflowTrigger, WorkflowInstance, WorkflowStepRun, WorkflowRun,
 *     WorkflowClone) as Zod schemas, for API responses + tests
 *   - legacy aliases (WorkflowStepKey, PauseWorkflowInputSchema,
 *     RunWorkflowInputSchema) so existing routes + the worker compile
 *     until their dedicated rewrite agents replace them
 */

import { z } from 'zod';

// ===========================================================================
// LEGACY — preserved so existing call sites keep compiling.
// Targeted for deletion once apps/worker/src/workflow.ts and
// apps/api/src/routes/workflows.ts are rewritten in later Phase 1 agents.
// ===========================================================================

export const LegacyWorkflowStepKeySchema = z.enum(['WF1-S1', 'WF1-S2', 'WF1-S3', 'WF1-S4']);
export type LegacyWorkflowStepKey = z.infer<typeof LegacyWorkflowStepKeySchema>;

// Back-compat alias — old name.
export const WorkflowStepKeySchema = LegacyWorkflowStepKeySchema;
export type WorkflowStepKey = LegacyWorkflowStepKey;

export const LegacyWorkflowInstanceStatusSchema = z.enum([
  'not_started',
  'waiting_step_1',
  'waiting_step_2',
  'waiting_step_3',
  'waiting_step_4',
  'complete',
  'paused',
  'blocked_missing_assignment',
  'blocked_missing_config',
]);
export type LegacyWorkflowInstanceStatus = z.infer<typeof LegacyWorkflowInstanceStatusSchema>;
export const WorkflowInstanceStatusSchema = LegacyWorkflowInstanceStatusSchema;
export type WorkflowInstanceStatus = LegacyWorkflowInstanceStatus;

export const LegacyWorkflowStepStatusSchema = z.enum([
  'not_started',
  'waiting',
  'notified',
  'cooldown',
  'complete',
  'skipped',
]);
export type LegacyWorkflowStepStatus = z.infer<typeof LegacyWorkflowStepStatusSchema>;
export const WorkflowStepStatusSchema = LegacyWorkflowStepStatusSchema;
export type WorkflowStepStatus = LegacyWorkflowStepStatus;

/**
 * The pre-redesign WorkflowDefinition row shape. The migration renames
 * the table to `Workflow`; this schema stays for back-compat reads of
 * the transitional `definitionJson` column.
 */
export const LegacyWorkflowDefinitionSchema = z.object({
  id: z.string(),
  organizationId: z.string().nullable(),
  key: z.string(),
  name: z.string(),
  version: z.number().int().positive(),
  isEnabled: z.boolean(),
  definitionJson: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type LegacyWorkflowDefinition = z.infer<typeof LegacyWorkflowDefinitionSchema>;
export const WorkflowDefinitionSchema = LegacyWorkflowDefinitionSchema;
export type WorkflowDefinition = LegacyWorkflowDefinition;

export const PauseWorkflowInputSchema = z.object({
  status: z.enum(['paused', 'resumed']),
  pausedUntil: z.string().datetime().optional(),
  reason: z.string().max(500).optional(),
});
export type PauseWorkflowInput = z.infer<typeof PauseWorkflowInputSchema>;

export const RunWorkflowInputSchema = z.object({
  scope: z.enum(['all_active', 'single']).default('all_active'),
  loanId: z.string().optional(),
});
export type RunWorkflowInput = z.infer<typeof RunWorkflowInputSchema>;

// ===========================================================================
// PHASE 1 — new row shapes mirroring the Prisma schema (Section 2).
// ===========================================================================

export const WorkflowVisibilitySchema = z.enum([
  'PRIVATE',
  'ORG_PUBLISHED',
  'ADMIN_PUBLISHED',
]);
export const WorkflowStatusSchema = z.enum(['DRAFT', 'ACTIVE', 'ARCHIVED']);
export const TriggerKindSchema = z.enum(['TIME', 'EVENT', 'CONDITION']);
export const StepCategorySchema = z.enum(['NOTIFY', 'WAIT', 'BRANCH', 'TASK', 'TERMINAL']);

/** Workflow row — body of GET /workflows/:id (header / metadata part). */
export const WorkflowSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  authorId: z.string(),
  visibility: WorkflowVisibilitySchema,
  status: WorkflowStatusSchema,

  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  tags: z.array(z.string()),
  attributionVisible: z.boolean(),

  version: z.number().int().positive(),
  publishedVersion: z.number().int().nullable(),
  supersededById: z.string().nullable(),

  appliesToProducts: z.array(z.string()),
  appliesToStates: z.array(z.string()),
  referencedFields: z.array(z.string()),

  activeInstanceCount: z.number().int().nonnegative(),

  clonedFromWorkflowId: z.string().nullable(),
  clonedFromVersion: z.number().int().nullable(),

  searchBlob: z.string().nullable(),

  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  publishedAt: z.string().datetime().nullable(),
  archivedAt: z.string().datetime().nullable(),
});
export type Workflow = z.infer<typeof WorkflowSchema>;

export const WorkflowStepSchema = z.object({
  id: z.string(),
  workflowId: z.string(),
  key: z.string(),
  name: z.string(),
  category: StepCategorySchema,
  order: z.number().int(),
  actions: z.array(z.unknown()), // ActionSpec[] — typed at the API layer
  guardJson: z.unknown().nullable(),
  cooldownHours: z.number().int().nonnegative(),
  isTerminal: z.boolean(),
});
export type WorkflowStepRow = z.infer<typeof WorkflowStepSchema>;

export const WorkflowTransitionSchema = z.object({
  id: z.string(),
  workflowId: z.string(),
  fromStepId: z.string().nullable(),
  toStepId: z.string(),
  label: z.string().nullable(),
  guardJson: z.unknown().nullable(),
  order: z.number().int(),
});
export type WorkflowTransitionRow = z.infer<typeof WorkflowTransitionSchema>;

export const WorkflowTriggerSchema = z.object({
  id: z.string(),
  workflowId: z.string(),
  entryStepId: z.string().nullable(),
  kind: TriggerKindSchema,
  enabled: z.boolean(),
  eventType: z.string().nullable(),
  cronExpr: z.string().nullable(),
  nextFireAt: z.string().datetime().nullable(),
  config: z.unknown(),
});
export type WorkflowTriggerRow = z.infer<typeof WorkflowTriggerSchema>;

export const WorkflowCloneSchema = z.object({
  id: z.string(),
  cloneWorkflowId: z.string(),
  sourceWorkflowId: z.string(),
  sourceVersion: z.number().int().positive(),
  sourceAuthorId: z.string(),
  clonedAt: z.string().datetime(),
});
export type WorkflowClone = z.infer<typeof WorkflowCloneSchema>;

/**
 * WorkflowInstance — renamed FK column workflowDefinitionId → workflowId.
 * `currentStepKey` is now a free-form string (the new step keys can be
 * anything matching STEP_KEY_REGEX), but the WF1-S1..WF1-S4 values from
 * MyLoanTracker still validate.
 */
export const WorkflowInstanceSchema = z.object({
  id: z.string(),
  loanId: z.string(),
  workflowId: z.string(),
  currentStepKey: z.string().nullable(),
  status: WorkflowInstanceStatusSchema,
  pausedUntil: z.string().datetime().nullable().optional(),
  lastEvaluatedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type WorkflowInstance = z.infer<typeof WorkflowInstanceSchema>;

export const WorkflowStepRunSchema = z.object({
  id: z.string(),
  workflowInstanceId: z.string(),
  stepKey: z.string(),
  status: WorkflowStepStatusSchema,
  firstTriggeredAt: z.string().datetime().nullable().optional(),
  lastTriggeredAt: z.string().datetime().nullable().optional(),
  completedAt: z.string().datetime().nullable().optional(),
  cooldownUntil: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type WorkflowStepRun = z.infer<typeof WorkflowStepRunSchema>;

/** WorkflowRun (extended per Section 2). */
export const WorkflowRunKindSchema = z.enum(['scheduled', 'manual', 'test_run']);
export type WorkflowRunKind = z.infer<typeof WorkflowRunKindSchema>;

export const WorkflowRunSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  workflowId: z.string(),
  workflowVersionId: z.string().nullable(),
  loanId: z.string().nullable(),
  kind: WorkflowRunKindSchema,
  status: z.string(),
  queuedAt: z.string().datetime(),
  startedAt: z.string().datetime().nullable(),
  finishedAt: z.string().datetime().nullable(),
  requestedByUserId: z.string().nullable(),
  options: z.unknown().nullable(),
  stepsExecuted: z.number().int().nonnegative(),
  error: z.string().nullable(),
});
export type WorkflowRunRow = z.infer<typeof WorkflowRunSchema>;
